export const API = "/api";
export const getToken = () => localStorage.getItem("token");
export const getUser = () => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } };

// Chunk 14C: capability cache populated by Login.js after /token succeeds.
// Shape: { user_id, username, roles: [...], capabilities: [...],
//          claim_capabilities: { cap_name: scope_name, ... } }
export const getPermissions = () => {
  try {
    const p = JSON.parse(localStorage.getItem("permissions"));
    return p || { roles: [], capabilities: [], claim_capabilities: {} };
  } catch {
    return { roles: [], capabilities: [], claim_capabilities: {} };
  }
};

// Does the logged-in user hold an admin-level capability?
export const can = (cap) => (getPermissions().capabilities || []).includes(cap);

// Re-fetch /me/permissions and refresh the cached snapshot.  Returns
// { changed } — true when the capability set differs from the cache — so a
// caller can re-render.  This lets a freshly granted/revoked role (e.g. a TPA
// role) take effect WITHOUT a manual log-out/in.  Failures keep the old cache.
export async function refreshPermissions() {
  if (!getToken()) return { changed: false };
  try {
    const fresh = await apiFetch("/me/permissions");
    if (fresh && Array.isArray(fresh.capabilities)) {
      const oldCaps = JSON.stringify((getPermissions().capabilities || []).slice().sort());
      const newCaps = JSON.stringify(fresh.capabilities.slice().sort());
      localStorage.setItem("permissions", JSON.stringify(fresh));
      return { changed: oldCaps !== newCaps };
    }
  } catch { /* keep the cached snapshot on any failure */ }
  return { changed: false };
}

// Does the user hold any of these admin-level capabilities?
export const canAny = (caps) => (caps || []).some(can);

// Does the user have a claim-level capability that applies to THIS claim?
// Evaluates scope predicates client-side from /me/permissions.  Server is
// still the authoritative gate — this is strictly for UI hinting.
export const canOnClaim = (cap, claim, user) => {
  const perms = getPermissions();
  const scope = (perms.claim_capabilities || {})[cap];
  if (!scope) return false;
  if (scope === "all_claims") return true;
  if (scope === "assigned_claims") {
    if (!claim || !user) return false;
    // Parties-4: "assigned" = lead adjuster OR any active claim_staff row.
    // claim_staff_user_ids is added to GET /claims/{id} response (Migration 026).
    if (claim.lead_adjuster_id === user.id) return true;
    return Array.isArray(claim.claim_staff_user_ids) && claim.claim_staff_user_ids.includes(user.id);
  }
  // Unknown / deferred scopes (e.g. client_claims) fall through to false;
  // the server re-checks anyway.
  return false;
};

// True if any role on this user is the system admin role.  Convenience for
// Admin-tab visibility where we don't want to enumerate every admin cap.
export const isSystemAdmin = () => (getPermissions().roles || []).includes("system_admin");

// All admin-ish roles that should see the Admin nav tab — any admin-level
// capability grants access to the tab, with sub-tabs inside gated by their
// specific cap so an Accountant sees Billing Rates only, a Manager sees
// the Review Queue only, system_admin sees everything including the
// System group (UI-NAV-1.5 added view_sysadmin_panel because the System
// sub-tabs — Project Status / Table Editor / System Settings — gate on it
// alone, and a user with only that cap should still see Admin in nav).
export const canViewAdminTab = () => canAny([
  "manage_users",
  "approve_companies",
  "approve_contacts",
  "manage_billing_rates",
  "manage_vca_sync",
  "manage_pipeline",
  "view_sysadmin_panel",
  "manage_website_publish",  // EMPLOYEE-PROFILE-1: website publish queue
]);

// UI-NAV-1: nav-peer visibility for the new 6-peer top-nav (memo §4).
// canViewOperations gates the Operations tab — operationally-capable users
// only.  Until UI-OPERATIONS-1 introduces dedicated Cap.VIEW_OPERATIONS, we
// piggyback on the union of existing operations-relevant capabilities so
// the right people see the new tab from day one without needing role grants.
export const canViewOperations = () => canAny([
  "manage_pipeline",
  "manage_vca_sync",
  "approve_companies",
  "approve_contacts",
  "manage_users",
  "manage_billing_rates",
  "admin_time_entry",   // OPS-1: Time Management surface lives in Operations
  "view_bill_intake",   // OPS-3: Bill Intake queue lives in Operations
  "manage_bill_intake",
  // NOTE (2026-06-17, Chunk B): code_payments + manage_reimbursement_intake
  // moved out of Operations to the Accounting console — see canViewAccounting.
]);

// canViewAccounting gates the new Accounting top-nav peer (Chunk B, 2026-06-17).
// Accounting = approvals / AR / payment coding / funds intake / trust / payroll.
// Until dedicated VIEW_* caps land, piggyback on the union of accounting-relevant
// capabilities. manage_users keeps the system admin able to see it. view_payroll
// / manage_payroll are introduced in Chunk C (Payroll gated area) — harmless to
// list now (canAny ignores caps the user doesn't hold).
export const canViewAccounting = () => canAny([
  "manage_invoicing",
  "code_payments",
  "manage_reimbursement_intake",
  "view_payroll",
  "manage_payroll",
  "manage_users",
]);

// canViewAnalytics gates the Analytics tab.  Memo §4 calls for "users with
// at least one role-scoped analytics view" — for v1 every logged-in user
// has at least the personal WIP view (MyNumbers), so this returns true for
// any authenticated user.  UI-ANALYTICS-1 will introduce Cap.VIEW_ANALYTICS
// proper and tighten this check.
export const canViewAnalytics = () => true;

// canViewMyInbox gates the top-level "My Inbox" surface (ADJUSTER-INBOX-1).
// Self-scoped: it only ever shows a user their OWN routed docs / review items /
// faxes, so it's offered to every internal claim-handling user (LD-ADJINBOX-003).
// Hidden only for a pure client_viewer (portal role, deferred). Server self-scopes
// regardless of this client-side check.
export const canViewMyInbox = () => {
  const roles = getPermissions().roles || [];
  return roles.some((r) => r !== "client_viewer");
};

// canViewPolicies gates the Company Policies top-nav tab (LD-POL-001..016). Held by the 7 internal
// workforce roles via VIEW_COMPANY_POLICIES; server is authoritative (defense in depth, Invariant #9).
export const canViewPolicies = () => can("view_company_policies");

// Stable list of role codes + display names for the Admin page dropdown.
// Keep this in sync with the public.roles table (migration 014 + 015).
// Client Viewer is hidden from internal-user role pickers until the portal
// ships — its is_internal=false row is the contract.
export const ROLE_OPTIONS = [
  { code: "adjuster",       label: "Adjuster / Claims Handler" },
  { code: "claims_support", label: "Claims Support" },
  { code: "accountant",     label: "Accountant" },
  { code: "claims_manager", label: "Claims Manager" },
  { code: "executive",      label: "Executive" },
  { code: "ownership",      label: "Ownership" },
  { code: "system_admin",   label: "System Admin" },
  // IA-TPA-SEPARATION-1: additive TPA roles (granted ON TOP of a user's IA roles).
  // tpa_user = works assigned TPA matters only; tpa_admin = opens + oversees all TPA.
  { code: "tpa_user",       label: "TPA User (assigned matters)" },
  { code: "tpa_admin",      label: "TPA Administrator (all TPA + create)" },
  // R-15: machine role for the bordereau bulk loader.  The server refuses it in
  // combination with any other role.  Not for people.
  { code: "bdx_loader",     label: "BDX Loader — SERVICE ACCOUNT ONLY (must be the only role)" },
];

// Chunk 16C — JWT sliding refresh. The backend issues 30-min tokens by
// default; we proactively call /token/refresh once the token is past 50%
// of its lifetime so an active session never expires mid-action. If the
// refresh fails we don't fight it — the next 401 will route through the
// existing logout flow below.
function jwtClaims(token) {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch { return null; }
}

let _refreshInFlight = false;
async function _maybeRefreshToken() {
  if (_refreshInFlight) return;
  const token = getToken();
  if (!token) return;
  const claims = jwtClaims(token);
  if (!claims || !claims.exp || !claims.iat) return;
  const nowSec = Math.floor(Date.now() / 1000);
  if (nowSec >= claims.exp) return;          // already expired — let 401 handle it
  const total = claims.exp - claims.iat;
  const elapsed = nowSec - claims.iat;
  if (elapsed * 2 < total) return;           // still in first half — no need to refresh
  _refreshInFlight = true;
  try {
    const res = await fetch(API + "/token/refresh", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      localStorage.setItem("token", data.access_token);
      // user + permissions are unchanged on refresh, so no need to re-fetch /me/permissions.
    }
  } catch {
    // Network error or similar — silently fall through.  Next 401 will logout.
  } finally {
    _refreshInFlight = false;
  }
}

export async function apiFetch(path, opts = {}) {
  // Fire-and-forget refresh check; don't await, don't block the actual request.
  _maybeRefreshToken();
  // Reporting Foundation (2026-05-18): opts.raw=true returns the raw Response
  // object (caller does .blob() / .text() as needed).  Used by the XLSX
  // export buttons so the browser handles the binary download via
  // Content-Disposition.  Default behavior is unchanged.
  const isRaw = !!opts.raw;
  const cleanOpts = { ...opts };
  delete cleanOpts.raw;
  const headers = isRaw
    ? { Authorization: `Bearer ${getToken()}`, ...cleanOpts.headers }
    : { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}`, ...cleanOpts.headers };
  const res = await fetch(API + path, {
    ...cleanOpts,
    headers,
    body: cleanOpts.body ? (isRaw ? cleanOpts.body : JSON.stringify(cleanOpts.body)) : undefined,
  });
  if (res.status === 401) {
    // Chunk 14E: if the user was impersonating, the 401 is most likely the
    // 30-min impersonation token expiring — restore their original session
    // instead of dumping them all the way to login.
    const orig = localStorage.getItem("original_token");
    if (orig) {
      const origUser = localStorage.getItem("original_user");
      const origPerms = localStorage.getItem("original_permissions");
      localStorage.setItem("token", orig);
      if (origUser) localStorage.setItem("user", origUser);
      if (origPerms) localStorage.setItem("permissions", origPerms);
      localStorage.removeItem("original_token");
      localStorage.removeItem("original_user");
      localStorage.removeItem("original_permissions");
    } else {
      localStorage.clear();
    }
    window.location.reload();
  }
  if (!res.ok) throw new Error(await res.text());
  return isRaw ? res : res.json();
}

// Date-only strings ("YYYY-MM-DD", e.g. a DATE column) must render as the
// local calendar date. `new Date("2026-05-31")` parses as UTC midnight, which
// in a negative-UTC timezone displays as the PREVIOUS day — so a time entry
// logged for the 31st showed as the 30th. Parse pure date strings as local;
// leave full timestamps to normal Date parsing.
const _parseDate = (d) => {
  const m = typeof d === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
};
export const fmt = {
  date: (d) => d ? _parseDate(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "—",
  datetime: (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—",
  money: (n) => n != null ? "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—",
  hours: (h) => h != null ? Number(h).toFixed(2) + " hrs" : "—",
};

export const statusColor = (s) => {
  if (!s) return "#94a3b8";
  const sl = s.toLowerCase();
  if (sl.includes("open") || sl.includes("active") || sl.includes("new")) return "#10b981";
  if (sl.includes("closed")) return "#64748b";
  if (sl.includes("hold")) return "#f59e0b";
  if (sl.includes("litigation") || sl.includes("denied")) return "#ef4444";
  if (sl.includes("pending")) return "#3b82f6";
  return "#94a3b8";
};

export const CLAIM_TYPES = ["Jones Act, P&I Crew","USL&H","State WC","P&I, Crew","Hull & Machinery","Cargo","Other"];
export const RESERVE_TYPES = ["Medical","Bill Review","Transportation","Maintenance","Indemnity","Legal","Adjusting","Settlement","Other"];
// Parties-3 (2026-04-29) — unified role list across financial-stake parties
// AND service providers, all stored in claim_parties.  Role-category split
// lives in code (this constant) rather than as a DB column — the role
// itself is free-text in the schema, no CHECK constraint.  Adding a new
// role means updating BOTH this list and the matching ROLE_CATEGORY entry.
export const FINANCIAL_PARTY_ROLES = [
  "Insured",
  "Rep",
  "Underwriter",
  "Reinsurer",
  "Broker",
  "TPA",
  "Vessel Owner",
  "Coverage Counsel",
  "Defense Counsel",
  "Claimant's Counsel",
  "Adjusting Partner",
  "Client Other",
];

export const SERVICE_PROVIDER_ROLES = [
  "Surveyor",
  "Repairer",
  "Expert Witness",
  "Treating Provider",
  "IME Provider",
  "Investigator",
  "Salvor",
  "Other",
];

// Backwards-compatible export — some legacy callers may still import
// PARTY_ROLES expecting the full list.  Keep it as the union.
export const PARTY_ROLES = [...FINANCIAL_PARTY_ROLES, ...SERVICE_PROVIDER_ROLES];

// Role -> category lookup.  Used by PartiesTab to filter rows into the
// Financial Parties section vs the Service Providers section.
export const ROLE_CATEGORY = (() => {
  const m = {};
  FINANCIAL_PARTY_ROLES.forEach(r => { m[r] = "financial"; });
  SERVICE_PROVIDER_ROLES.forEach(r => { m[r] = "service"; });
  return m;
})();

// Service-provider engagement status enum (matches DB CHECK constraint in 025).
export const ENGAGEMENT_STATUSES = [
  "Contacted",
  "Engaged",
  "Active",
  "Completed",
  "Disengaged",
];
